"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useAction, useConvexAuth, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { api } from "@convex/_generated/api";
import { navigateToDocument } from "@/lib/document-navigation";
import { captureError } from "@/lib/error-tracking";
import type { PricingTier, BillingInterval, TierKey } from "../data";
import { PricingCTA, PricingCardContent } from "./pricing-card-sections";

import { canManageCompanyBillingFor } from "@shared/auth/access";
import type { SubscriptionTier } from "@shared/subscriptions/tiers";
import type { Id } from "@convex/_generated/dataModel";
import { settingsHref } from "@shared/routing/settings-path";

function convexErrorDataMessage(data: unknown): string | undefined {
  if (typeof data !== "object" || data === null || !("message" in data)) {
    return undefined;
  }
  return typeof data.message === "string" ? data.message : undefined;
}

interface PricingCardProps {
  tier: PricingTier;
  billingInterval: BillingInterval;
  onSelect?: (tierKey: TierKey) => void;
  isSelected?: boolean;
  disabled?: boolean;
  onSetupCheckout?: (planId: string) => void;
  onSetupFreePlan?: () => void;
  onError?: (message: string) => void;
  currentTierKey?: SubscriptionTier;
  currentInterval?: BillingInterval;
  onChangePlan?: (planId: string) => void;
  onDowngradeToFree?: () => void;
  isChangingPlan?: boolean;
  checkoutReturnTo?: string;
}

interface PricingCtaContext {
  tier: PricingTier;
  planId: string;
  billingInterval: BillingInterval;
  currentInterval?: BillingInterval;
  currentTierKey?: SubscriptionTier;
  isPaidTier: boolean;
  isFree: boolean;
  isEnterprise: boolean;
  isSetupMode: boolean;
  showCheckoutButton: boolean;
  isLoading: boolean;
  disabled?: boolean;
  isChangingPlan?: boolean;
  onChangePlan?: (planId: string) => void;
  onDowngradeToFree?: () => void;
  onClick: () => void;
  switchVariant: "primary" | "outline";
  ctaClassName: string;
}

function currentPlanCta({
  isPaidTier,
  onChangePlan,
  currentInterval,
  billingInterval,
  switchVariant,
  planId,
  isChangingPlan,
  ctaClassName,
}: PricingCtaContext) {
  return isPaidTier &&
    onChangePlan &&
    currentInterval &&
    currentInterval !== billingInterval ? (
    <Button
      type="button"
      variant={switchVariant}
      onClick={() => onChangePlan(planId)}
      disabled={isChangingPlan}
      className={ctaClassName}
    >
      Switch to {billingInterval} billing
    </Button>
  ) : (
    <div className="inline-flex min-h-11 items-center justify-center gap-2 border border-border bg-muted px-4 py-3 text-sm font-medium text-muted-foreground sm:py-2.5">
      Current plan
    </div>
  );
}

function otherPlanCta(context: PricingCtaContext) {
  const { tier, planId, isPaidTier, isFree, currentTierKey, disabled } =
    context;
  const { isChangingPlan, onChangePlan, onDowngradeToFree } = context;
  const { switchVariant, ctaClassName } = context;
  return isFree && currentTierKey !== undefined ? (
    <Button
      type="button"
      variant="outline"
      onClick={onDowngradeToFree}
      disabled={isChangingPlan || disabled || !onDowngradeToFree}
      className={ctaClassName}
    >
      Downgrade to Free
    </Button>
  ) : isPaidTier && onChangePlan ? (
    <Button
      type="button"
      variant={switchVariant}
      onClick={() => onChangePlan(planId)}
      disabled={isChangingPlan}
      className={ctaClassName}
    >
      Switch to {tier.name}
    </Button>
  ) : (
    <PricingCTA
      tier={tier}
      isEnterprise={context.isEnterprise}
      isFree={isFree}
      isSetupMode={context.isSetupMode}
      isPaidTier={isPaidTier}
      showCheckoutButton={context.showCheckoutButton}
      isLoading={context.isLoading}
      disabled={disabled}
      onClick={context.onClick}
    />
  );
}

function tierKind(tier: PricingTier) {
  const isEnterprise = tier.priceMonthly === null;
  const isFree = tier.priceMonthly === 0;
  const isPaidTier = !isEnterprise && !isFree;
  return { isEnterprise, isFree, isPaidTier };
}

function pricingCardClasses(
  tier: PricingTier,
  {
    isSelectionMode,
    isEnterprise,
    disabled,
    isSelected,
    isCurrent,
  }: {
    isSelectionMode: boolean;
    isEnterprise: boolean;
    disabled?: boolean;
    isSelected?: boolean;
    isCurrent: boolean;
  },
) {
  return cn(
    "relative flex flex-col border bg-background transition-all",
    tier.isPopular && "-translate-y-2",
    isSelectionMode && !isEnterprise && "cursor-pointer",
    isSelectionMode && disabled && "opacity-50 cursor-not-allowed",
    isSelected || isCurrent
      ? "border-primary ring-1 ring-primary"
      : tier.isPopular && !isSelectionMode
        ? "border-primary ring-1 ring-primary"
        : "border-border",
  );
}

const CTA_CLASS_NAME = "rounded-none px-4 py-3 min-h-11 sm:py-2.5";

function checkoutErrorMessage(error: unknown): string {
  return error instanceof ConvexError
    ? typeof error.data === "string"
      ? error.data
      : (convexErrorDataMessage(error.data) ?? "Failed to start checkout")
    : error instanceof Error
      ? error.message
      : "Failed to start checkout";
}

function usePricingCheckout(
  companyId: Id<"companies"> | undefined,
  planId: string,
  { checkoutReturnTo, onError }: PricingCardProps,
) {
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const createCheckout = useAction(
    api.subscriptions.actions.createSubscriptionCheckout,
  );

  const handleCheckout = async () => {
    if (!companyId) return;
    setIsCheckoutLoading(true);
    try {
      const result = await createCheckout({
        companyId,
        planId,
        returnTo: checkoutReturnTo,
      });
      if (result?.url) {
        window.location.href = result.url;
      } else {
        onError?.("Failed to create checkout session");
      }
    } catch (error) {
      if (!(error instanceof ConvexError)) {
        captureError(error, {
          tags: { surface: "pricing-card", operation: "create-checkout" },
          extra: { planId },
        });
      }
      const message = checkoutErrorMessage(error);
      onError?.(message);
    } finally {
      setIsCheckoutLoading(false);
    }
  };
  return { isCheckoutLoading, handleCheckout };
}

function usePricingBillingContext(enabled: boolean) {
  const company = useQuery(
    api.companies.queries.getOptionalUserCompany,
    enabled ? {} : "skip",
  );
  const currentUser = useQuery(
    api.users.queries.current,
    enabled ? {} : "skip",
  );
  const canManageBilling = canManageCompanyBillingFor(
    currentUser?.user ?? {},
    company?.role,
  );
  return { company, currentUser, canManageBilling };
}

interface PricingClickContext {
  planId: string;
  isEnterprise: boolean;
  isFree: boolean;
  isSetupMode: boolean;
  isAuthenticated: boolean;
  hasCompany: boolean;
  canManageBilling: boolean;
  onSetupCheckout?: (planId: string) => void;
  onSetupFreePlan?: () => void;
  handleCheckout: () => Promise<void>;
}

function usePricingClick(context: PricingClickContext) {
  const router = useRouter();
  const { user: authUser } = useAuth();
  const { planId, isEnterprise, isFree, isSetupMode } = context;
  const { onSetupCheckout, onSetupFreePlan } = context;
  return async () => {
    if (isEnterprise) return;
    if (isSetupMode) {
      if (isFree && onSetupFreePlan) {
        onSetupFreePlan();
      } else if (!isFree && onSetupCheckout) {
        onSetupCheckout(planId);
      }
      return;
    }
    if (isFree) return;
    if (!context.isAuthenticated || !authUser) {
      navigateToDocument(`/sign-up?plan=${planId}`);
      return;
    }
    if (!context.hasCompany) {
      router.push(`/dashboard?plan=${planId}`);
      return;
    }
    if (!context.canManageBilling) {
      router.push(settingsHref("plans"));
      return;
    }
    await context.handleCheckout();
  };
}

function usePricingCta(props: PricingCardProps, isSelectionMode: boolean) {
  const { tier, billingInterval } = props;
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  const { isEnterprise, isFree, isPaidTier } = tierKind(tier);
  const isSetupMode = !!props.onSetupCheckout || !!props.onSetupFreePlan;
  const planId = `${tier.tierKey}-${billingInterval}`;
  const { company, currentUser, canManageBilling } = usePricingBillingContext(
    isAuthenticated && !isSelectionMode,
  );
  const { isCheckoutLoading, handleCheckout } = usePricingCheckout(
    company?._id,
    planId,
    props,
  );
  const handleClick = usePricingClick({
    ...props,
    planId,
    isEnterprise,
    isFree,
    isSetupMode,
    isAuthenticated,
    hasCompany: !!company,
    canManageBilling,
    handleCheckout,
  });

  const isBillingContextLoading =
    isAuthenticated &&
    !isSelectionMode &&
    (company === undefined || currentUser === undefined);
  const ctaContext: PricingCtaContext = {
    ...props,
    planId,
    isPaidTier,
    isFree,
    isEnterprise,
    isSetupMode,
    showCheckoutButton:
      isPaidTier && isAuthenticated && !!company && canManageBilling,
    isLoading:
      isCheckoutLoading ||
      (isPaidTier && (authLoading || isBillingContextLoading)),
    onClick: handleClick,
    switchVariant: tier.isPopular ? "primary" : "outline",
    ctaClassName: CTA_CLASS_NAME,
  };
  return ctaContext;
}

export function PricingCard(props: PricingCardProps) {
  const { tier, billingInterval, onSelect, isSelected, disabled } = props;
  const isSelectionMode = !!onSelect;
  const { isEnterprise } = tierKind(tier);
  const ctaContext = usePricingCta(props, isSelectionMode);
  const isCurrent = props.currentTierKey === tier.tierKey;
  const renderCTA = () =>
    isCurrent ? currentPlanCta(ctaContext) : otherPlanCta(ctaContext);

  const cardClasses = pricingCardClasses(tier, {
    isSelectionMode,
    isEnterprise,
    disabled,
    isSelected,
    isCurrent,
  });

  if (isSelectionMode && !isEnterprise) {
    return (
      <button
        type="button"
        onClick={() => onSelect(tier.tierKey)}
        disabled={disabled}
        className={cn(cardClasses, "text-left")}
      >
        <PricingCardContent
          tier={tier}
          billingInterval={billingInterval}
          isSelectionMode={isSelectionMode}
          renderCTA={renderCTA}
        />
      </button>
    );
  }

  return (
    <div className={cardClasses}>
      <PricingCardContent
        tier={tier}
        billingInterval={billingInterval}
        isSelectionMode={isSelectionMode}
        renderCTA={renderCTA}
        onSelectEnterprise={(event) => event.stopPropagation()}
      />
    </div>
  );
}
